import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@fluentui/react-components";
import type { SourceStatusMapping, Status } from "@onetasklist/shared";
import {
  type StatusMappingItem,
  useStatusMappingSectionViewModel,
  useStatusMappingSectionText,
} from "@onetasklist/shared/sources/statusMapping";
import { SettingsSectionCard } from "./SettingsSectionCard";
import { SourceMultiTagPicker } from "./SourceMultiTagPicker";
import "./StatusMappingSection.scss";

export type { StatusMappingItem };

interface StatusMappingSectionProps {
  sectionNumber: number;
  items: StatusMappingItem[];
  mappings: SourceStatusMapping[];
  boardStatuses: Status[];
  onChange: (mappings: SourceStatusMapping[]) => void;
  /** "card" (default) wraps the table in a numbered SettingsSectionCard; "bare" renders just the toggle + table, for embedding in a Dialog. */
  variant?: "card" | "bare";
}

export const StatusMappingSection: React.FC<StatusMappingSectionProps> = ({
  sectionNumber,
  items,
  mappings,
  boardStatuses,
  onChange,
  variant = "card",
}) => {
  const { t } = useTranslation();
  const text = useStatusMappingSectionText(t);
  const vm = useStatusMappingSectionViewModel({ items, mappings, boardStatuses, onChange });

  if (items.length === 0) {
    return null;
  }

  const toggleAction = (
    <Button appearance="subtle" size="small" onClick={vm.toggleViewMode}>
      {vm.viewMode === "source" ? text.switchToColumnView : text.switchToSourceView}
    </Button>
  );

  const boardStatusOptions = boardStatuses.map((s) => ({ id: s.id, label: s.name }));
  const sourceItemOptions = items.map((i) => ({ id: i.id, label: i.name }));

  const table = (
    <table className="status-mapping-table">
      <thead>
        <tr>
          <th>{vm.viewMode === "source" ? text.sourceStatusHeader : text.boardColumnHeader}</th>
          <th>{vm.viewMode === "source" ? text.boardColumnHeader : text.sourceStatusHeader}</th>
        </tr>
      </thead>
      <tbody>
        {vm.viewMode === "source"
          ? items.map((item) => {
              const selectedIds = vm.getMappedStatusIds(item.id);
              const query = vm.queries[item.id] ?? "";
              return (
                <tr key={item.id}>
                  <td className="status-mapping-table__source-name">{item.name}</td>
                  <td>
                    <SourceMultiTagPicker
                      selectedIds={selectedIds}
                      query={query}
                      placeholder={text.columnPlaceholder}
                      options={boardStatusOptions}
                      onQueryChange={(v) => vm.setQuery(item.id, v)}
                      onSelectionChange={(ids) => {
                        vm.setQuery(item.id, "");
                        vm.handleSelectionChange(item, ids);
                      }}
                      minQueryLength={0}
                    />
                  </td>
                </tr>
              );
            })
          : boardStatuses.map((col) => {
              const selectedIds = vm.getMappedSourceIds(col.id);
              const query = vm.queries[col.id] ?? "";
              return (
                <tr key={col.id}>
                  <td className="status-mapping-table__source-name">{col.name}</td>
                  <td>
                    <SourceMultiTagPicker
                      selectedIds={selectedIds}
                      query={query}
                      placeholder={text.sourcePlaceholder}
                      options={sourceItemOptions}
                      onQueryChange={(v) => vm.setQuery(col.id, v)}
                      onSelectionChange={(ids) => {
                        vm.setQuery(col.id, "");
                        vm.handleColumnSelectionChange(col, ids);
                      }}
                      minQueryLength={0}
                    />
                  </td>
                </tr>
              );
            })}
      </tbody>
    </table>
  );

  if (variant === "bare") {
    return (
      <div className="status-mapping-section__bare">
        <div className="status-mapping-section__bare-toolbar">{toggleAction}</div>
        {table}
      </div>
    );
  }

  return (
    <SettingsSectionCard sectionOffset={sectionNumber} title={text.sectionTitle} action={toggleAction}>
      {table}
    </SettingsSectionCard>
  );
};
